import { useState } from 'react';
import type { FlowTableSelectionConfig } from '../types';
import { computeRangeIds } from './selection';

/** The slice of a React mouse event the gestures read — keeps the hook table-agnostic (`<tr>` in both tables). */
interface ModifierMouseEvent {
  shiftKey: boolean;
  metaKey: boolean;
  ctrlKey: boolean;
  preventDefault: () => void;
}

/**
 * Row-selection gestures shared by `FlowTable` and the V2 `LedgerTable`. Owns the one piece of
 * selection state a table has — the shift-click anchor ("last explicitly toggled row id"); the
 * selected Set stays the caller's (`selection.selectedIds`).
 *
 * - `onRowClick`: Cmd/Ctrl/Shift-click anywhere on a row operate selection only. Returns true when
 *   it handled the click, so the caller returns before its expand/navigate branch.
 * - `onCheck`: the checkbox itself. A shift-click selects the range from the anchor, like a
 *   shift-click on the row; a plain click toggles one. Both move the anchor.
 * - `onRowMouseDown`: a text selection starts on mousedown, so `preventDefault` on the later click
 *   is too late to stop shift-click highlighting rows; cancel it here instead (no `select-none`,
 *   which would also block copying cell text on a plain drag).
 *
 * The anchor is not a StrictMode lifetime ref — a stale one just re-resolves against the current
 * order on the next shift-click (see docs/v4-system/strictmode-resilient-effects.md).
 */
export function useRowSelectionGestures<Row>({ selection, loadedIds }: {
  selection: FlowTableSelectionConfig<Row> | undefined;
  loadedIds: string[];
}) {
  const [anchorId, setAnchorId] = useState<string | null>(null);

  const toggleRangeTo = (id: string) => {
    selection?.onToggleRange(computeRangeIds(loadedIds, anchorId, id));
    setAnchorId(id);
  };

  const onRowClick = (e: ModifierMouseEvent, id: string): boolean => {
    if (!selection) return false;
    if (e.shiftKey) {
      e.preventDefault();
      toggleRangeTo(id);
      return true;
    }
    if (e.metaKey || e.ctrlKey) {
      e.preventDefault();
      selection.onToggle(id);
      setAnchorId(id);
      return true;
    }
    return false;
  };

  const onCheck = (id: string, shiftKey: boolean) => {
    if (!selection) return;
    if (shiftKey) {
      toggleRangeTo(id);
      return;
    }
    selection.onToggle(id);
    setAnchorId(id);
  };

  const onRowMouseDown = (e: Pick<ModifierMouseEvent, 'shiftKey' | 'preventDefault'>) => {
    if (selection && e.shiftKey) e.preventDefault();
  };

  return { onRowClick, onCheck, onRowMouseDown };
}
